import { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  makeStyles,
  tokens,
  Text,
  Spinner,
  Badge,
} from '@fluentui/react-components';
import { DeleteRegular, AddRegular } from '@fluentui/react-icons';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useRecurringRules, useDeleteRecurringRule } from '../../hooks/useRecurring';
import { DAYS_OF_WEEK, type RecurringRule } from '@cp-parkingslots/shared';
import RecurringFormDialog from './RecurringFormDialog';

const useStyles = makeStyles({
  surface: {
    maxWidth: '550px',
    width: '100%',
    '@media (max-width: 599px)': {
      maxWidth: '100%',
      margin: tokens.spacingHorizontalS,
      maxHeight: '90vh',
    },
  },
  dialogContent: {
    '@media (max-width: 599px)': {
      maxHeight: '60vh',
      overflowY: 'auto',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  ruleItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    gap: tokens.spacingHorizontalS,
    '@media (max-width: 599px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: tokens.spacingVerticalS,
    },
  },
  ruleInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flex: 1,
    minWidth: 0,
  },
  ruleMain: {
    fontWeight: tokens.fontWeightSemibold,
    '@media (max-width: 599px)': {
      fontSize: tokens.fontSizeBase300,
    },
  },
  ruleDetails: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  empty: {
    textAlign: 'center',
    padding: tokens.spacingVerticalXL,
    color: tokens.colorNeutralForeground2,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalM,
    gap: tokens.spacingHorizontalS,
    '@media (max-width: 599px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: tokens.spacingVerticalS,
    },
  },
  headerText: {
    color: tokens.colorNeutralForeground2,
    '@media (max-width: 599px)': {
      textAlign: 'center',
    },
  },
  addButton: {
    flexShrink: 0,
    '@media (max-width: 599px)': {
      width: '100%',
    },
  },
  inactiveBadge: {
    marginLeft: tokens.spacingHorizontalS,
  },
  deleteButton: {
    flexShrink: 0,
    '@media (max-width: 599px)': {
      width: '100%',
    },
  },
  actions: {
    '@media (max-width: 599px)': {
      '& button': {
        width: '100%',
      },
    },
  },
});

interface RecurringRulesDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function RecurringRulesDialog({
  open,
  onClose,
}: RecurringRulesDialogProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [showAddForm, setShowAddForm] = useState(false);
  const { data: rules, isLoading } = useRecurringRules();
  const deleteRule = useDeleteRecurringRule();

  const getDayName = (dayOfWeek: number) => {
    const dayKey = DAYS_OF_WEEK.find((d) => d.value === dayOfWeek)?.labelKey;
    return dayKey ? t(dayKey as never) : '';
  };

  const handleDelete = async (rule: RecurringRule) => {
    if (
      confirm(
        t('recurringRules.confirmDelete', { day: getDayName(rule.dayOfWeek) })
      )
    ) {
      await deleteRule.mutateAsync(rule.id);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
        <DialogSurface className={styles.surface}>
          <DialogBody>
            <DialogTitle>{t('recurringRules.title')}</DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <div className={styles.header}>
                <Text size={200} className={styles.headerText}>
                  {t('recurringRules.description')}
                </Text>
                <Button
                  className={styles.addButton}
                  appearance="primary"
                  icon={<AddRegular />}
                  size="small"
                  onClick={() => setShowAddForm(true)}
                >
                  {t('recurringRules.newRule')}
                </Button>
              </div>

              {isLoading ? (
                <div style={{ textAlign: 'center', padding: tokens.spacingVerticalXL }}>
                  <Spinner label={t('recurringRules.loading')} />
                </div>
              ) : !rules?.length ? (
                <div className={styles.empty}>
                  <Text>{t('recurringRules.empty')}</Text>
                </div>
              ) : (
                <div className={styles.content}>
                  {rules.map((rule) => (
                    <div key={rule.id} className={styles.ruleItem}>
                      <div className={styles.ruleInfo}>
                        <Text className={styles.ruleMain}>
                          {t('recurringRules.every')} {getDayName(rule.dayOfWeek)}
                          {' • '}
                          {rule.startHour}:00 - {rule.endHour}:00
                          {!rule.isActive && (
                            <Badge
                              className={styles.inactiveBadge}
                              appearance="outline"
                              color="warning"
                              size="small"
                            >
                              {t('recurringRules.inactive')}
                            </Badge>
                          )}
                        </Text>
                        <Text className={styles.ruleDetails}>
                          {t(`slots.${rule.slotId}` as const)}
                          {' • '}
                          {format(new Date(rule.validFrom), 'd.M.yy')} -{' '}
                          {format(new Date(rule.validUntil), 'd.M.yy')}
                        </Text>
                      </div>
                      <Button
                        className={styles.deleteButton}
                        appearance="subtle"
                        icon={<DeleteRegular />}
                        onClick={() => handleDelete(rule)}
                        disabled={deleteRule.isPending}
                      >
                        {t('reservation.delete')}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </DialogContent>
            <DialogActions className={styles.actions}>
              <Button appearance="secondary" onClick={onClose}>
                {t('reservation.close')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <RecurringFormDialog
        open={showAddForm}
        onClose={() => setShowAddForm(false)}
      />
    </>
  );
}
